import { useState } from 'react';
import {
  Accordion,
  Anchor,
  Button,
  Card,
  Heading,
  List,
  Page,
  Paragraph,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { EXTERNAL_URLS, getEditRoute, getExternalUrl, TERMS_OF_USE_URL, TERMS_RENTER_URL } from '../../constants/urls';
import { RENTAL_APPLICATION_PRICE } from '../../constants/applicationsConstants';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { GetTermSetDocument, markAcceptedTermsDocument, TermId } from '../../graphql/queries';
import { BlockLoadingMask } from '../../styles/styled-components';
import { STEP_IDS } from '../../constants/wizard';
import { logError } from '@zg-rentals/log-error';
import {
  emitOnboardingEvent,
  emitParticipatingPropertiesLinkEvent,
  emitViewStepTriggerEvent,
} from '../../analytics/clickstreamEvents';
import { useApplicationContextOrUndefined } from '../../contexts/ApplicationContext';
import { Application } from '../../images/Application';
import { useEffectOnce } from 'react-use';

const GET_STARTED_BUTTON_TEXT = 'Get started';

export function ApplicationLanding({ applicationId }: { applicationId: string }) {
  const router = useRouter();
  const applicationContext = useApplicationContextOrUndefined();
  const { enqueueToast } = useToast();
  const [markAcceptedTermsMutation] = useMutation(markAcceptedTermsDocument, {
    refetchQueries: [GetTermSetDocument, 'GetTermSet'],
  });
  const [isLoadingApplication, setIsLoadingApplication] = useState(false);

  useEffectOnce(() => {
    emitViewStepTriggerEvent(applicationContext, 'application_onboarding_firsttime_user');
  });

  async function acceptRentalUserTerms() {
    emitOnboardingEvent(applicationContext);
    try {
      setIsLoadingApplication(true);
      const response = await markAcceptedTermsMutation({
        variables: {
          terms: [
            {
              termId: TermId.TERMS_ZILLOW_RENTAL_USER,
              version: 0,
            },
          ],
        },
      });
      if (response?.data?.markAcceptedTerms?.length === 1) {
        router.push(getEditRoute({ applicationId, stepId: STEP_IDS.LEGAL_NAME }));
      } else {
        setIsLoadingApplication(false);
        enqueueToast(<Toast tone="critical">Unable to accept terms and start your application, try again</Toast>);
      }
    } catch (err) {
      logError({ error: err, context: 'ApplicationLanding - markAcceptedTermsMutation error' });
      setIsLoadingApplication(false);
      enqueueToast(<Toast tone="critical">Unable to accept terms and start your application, try again</Toast>);
    }
  }

  return (
    <Page.Root css={{ backgroundColor: 'white' }}>
      <Page.Content>
        <BlockLoadingMask loading={isLoadingApplication}>
          <Application />
          <Heading level={1} textStyle="responsive.heading-xs" css={{ marginBottom: 'xs', marginTop: 'sm' }}>
            Cut out extra forms and fees when you apply with Zillow
          </Heading>
          <Paragraph css={{ marginBottom: 'md' }}>
            Get started today — you won’t be charged until you apply to your first property.
          </Paragraph>
          <Card css={{ marginBottom: 'md' }} elevated={false} outlined={false} tone="soft">
            <Heading level={2} textStyle="responsive.body-lg-bold">
              For {RENTAL_APPLICATION_PRICE} you’ll get:
            </Heading>
            <List.Root>
              <List.Item>Experian credit report</List.Item>
              <List.Item>CIC background check</List.Item>
              <List.Item>Reuse for 30 days with participating properties you find on or off Zillow</List.Item>
            </List.Root>
          </Card>
          <Accordion.Root css={{ marginBottom: 'md' }}>
            <Accordion.Item value="reuse-application">
              <Accordion.Header>
                <Heading level={2} textStyle="responsive.body-lg-bold">
                  Where can I reuse my Zillow Application?
                </Heading>
              </Accordion.Header>
              <Accordion.Panel>
                <Paragraph>
                  You can reuse your application on{' '}
                  <Anchor
                    href={getExternalUrl(EXTERNAL_URLS.RENTALS_SEARCH_PAGE_URL)}
                    onClick={() => emitParticipatingPropertiesLinkEvent(applicationContext)}
                  >
                    participating properties
                  </Anchor>{' '}
                  that can be identified by the <strong>Apply instantly</strong> badge. For properties without the
                  badge, we suggest checking with the rental manager to see if they will accept your Zillow Application.
                </Paragraph>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion.Root>
          <Button tone="brand" emphasis="filled" onClick={acceptRentalUserTerms}>
            {GET_STARTED_BUTTON_TEXT}
          </Button>
          <Paragraph textStyle="fineprint" css={{ marginTop: 'xs' }}>
            By clicking <strong>{GET_STARTED_BUTTON_TEXT}</strong>, you agree to comply with the{' '}
            <Anchor href={TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer">
              Terms of Use
            </Anchor>
            {' and the '}
            <Anchor href={TERMS_RENTER_URL} target="_blank" rel="noopener noreferrer">
              Rental User Terms
            </Anchor>
            {'.'}
          </Paragraph>
        </BlockLoadingMask>
      </Page.Content>
    </Page.Root>
  );
}
